import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { activateUser } from '../../api/auth';
import { t } from '../../utils/translations';
import { styles } from './ActivateAccount.styles';

type ActivationStatus = 'loading' | 'success' | 'error';

const ActivateAccount: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ActivationStatus>('loading');

  useEffect(() => {
    const activateAccount = async () => {
      if (!id) {
        setStatus('error');
        return;
      }

      try {
        await activateUser(id);
        setStatus('success');
      } catch (error) {
        setStatus('error');
      }
    };

    activateAccount();
  }, [id]);

  const handleLoginRedirect = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="sm">
      <Box sx={styles.container}>
        {status === 'loading' && (
          <Box sx={styles.contentBox}>
            <CircularProgress size={80} sx={styles.loader} />
            <Typography variant="h5" sx={styles.title}>
              {t('activateAccount.activating')}
            </Typography>
          </Box>
        )}

        {status === 'success' && (
          <Box sx={styles.contentBox}>
            <CheckCircleOutlineIcon sx={styles.successIcon} />
            <Typography variant="h4" sx={styles.title}>
              {t('activateAccount.success')}
            </Typography>
            <Typography variant="body1" sx={styles.description}>
              {t('activateAccount.successDescription')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleLoginRedirect}
              sx={styles.button}
            >
              {t('activateAccount.loginButton')}
            </Button>
          </Box>
        )}

        {status === 'error' && (
          <Box sx={styles.contentBox}>
            <ErrorOutlineIcon sx={styles.errorIcon} />
            <Typography variant="h4" sx={styles.title}>
              {t('activateAccount.error')}
            </Typography>
            <Typography variant="body1" sx={styles.description}>
              {t('activateAccount.errorDescription')}
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ActivateAccount;
